import jsPDF from 'jspdf';
import montserrat600Url from './proof/Montserrat-600.ttf';
import montserrat700Url from './proof/Montserrat-700.ttf';
import montserrat800Url from './proof/Montserrat-800.ttf';
import nunitoSans400Url from './proof/NunitoSans-400.ttf';
import logoUrl from './proof/logo.png';

/*
 * The design proof ("ontwerpvoorbeeld") a customer gets when they download their
 * design as PDF: one A4 page in the shop's house style, built after the agreed
 * template (pdf_download/ontwerpbewijs-ronde-7.html). Positions are the
 * template's, in millimetres, measured off the page it renders.
 *
 * Text, shapes and the logo are drawn as PDF objects so they stay sharp in
 * print. The tiles - shirt photo, watermark and the "VOORBEELD" band - are
 * composed on a canvas first: the watermark has to sit over the design, and a
 * picture is the one place it cannot be lifted off again.
 *
 * Variable: SKU, colour, print method, a picture per side and which sides carry
 * a design. Everything else on the page is fixed copy.
 */

const PT = 25.4 / 72; // mm per point
const PX = 8; // canvas pixels per mm for the tiles, about 200 dpi

const COLOR = {
	cyan: '#009EE2', cyanDark: '#0079AC', cyanLight: '#F0F9FE',
	magenta: '#E5007E',
	ink: '#101820', text: '#3C4854', weak: '#78838F', faint: '#A3ACB6',
	border: '#E1E6EC', field: '#F4F6F9', star: '#FFB400', white: '#FFFFFF'
};

// Vertical metrics (ascender, descender per em) of the two faces, for putting a
// baseline where CSS would.
const METRICS = { Montserrat: [0.968, 0.251], NunitoSans: [1.011, 0.353] };

// The weights the template uses. Its body face is "Nunito Sans" at 500, which
// browsers draw with the 400 they have; this does the same.
const FONT_FILES = [
	['Montserrat', '600', montserrat600Url],
	['Montserrat', '700', montserrat700Url],
	['Montserrat', '800', montserrat800Url],
	['NunitoSans', '400', nunitoSans400Url]
];
// The family the tile canvas draws with, registered under a name of its own so
// it cannot pick up a different "Montserrat" the page may have loaded.
const CANVAS_FAMILY = 'SD Proof Montserrat';

const SIDE_ORDER = ['front', 'back', 'left', 'right'];
const SIDE_NAMES = { front: 'Voorkant', back: 'Achterkant', left: 'Linkermouw', right: 'Rechtermouw' };
const MONTHS = ['januari', 'februari', 'maart', 'april', 'mei', 'juni', 'juli', 'augustus', 'september', 'oktober', 'november', 'december'];

export function sideName(side) {
	return SIDE_NAMES[side] || (String(side).charAt(0).toUpperCase() + String(side).slice(1));
}

export function dutchDate(date) {
	return `${date.getDate()} ${MONTHS[date.getMonth()]} ${date.getFullYear()}`;
}

// Which side is shown large: the first printed one, in the fixed order front,
// back, left, right; with nothing printed the front. The page has room for one
// large picture and three small ones, so a fifth side is left off.
export function orderSides(sides) {
	const rank = s => {
		const i = SIDE_ORDER.indexOf(s.side);
		return i === -1 ? SIDE_ORDER.length : i;
	};
	const sorted = sides.slice().sort((a, b) => rank(a) - rank(b));
	return sorted.filter(s => s.printed).concat(sorted.filter(s => !s.printed)).slice(0, 4);
}

// Tile rectangles for the large picture and the column beside it, inside the
// zone from `top` to `bottom`. With a single side the large tile takes the full
// width rather than leaving an empty column next to it.
export function tileLayout(count, top, bottom) {
	const height = bottom - top;
	if (count <= 1) return [{ x: 14, y: top, w: 182, h: height, large: true }];
	const rowH = (height - 2 * 4) / 3;
	const tiles = [{ x: 14, y: top, w: 126, h: height, large: true }];
	for (let i = 1; i < count; i++) {
		tiles.push({ x: 144, y: top + (i - 1) * (rowH + 4), w: 52, h: rowH, large: false });
	}
	return tiles;
}

/* ---------- assets ---------- */

let assetsPromise = null;

function arrayBufferToBase64(buffer) {
	const bytes = new Uint8Array(buffer);
	let binary = '';
	for (let i = 0; i < bytes.length; i += 0x8000) {
		binary += String.fromCharCode.apply(null, bytes.subarray(i, i + 0x8000));
	}
	return btoa(binary);
}

async function fetchBuffer(url) {
	const res = await fetch(url);
	if (!res.ok) throw new Error(`proof: could not load ${url} (${res.status})`);
	return res.arrayBuffer();
}

// Fonts and logo, fetched once and kept: they only come along when someone
// downloads a PDF, not with every visit to the editor.
function loadAssets() {
	if (!assetsPromise) {
		assetsPromise = (async () => {
			const buffers = await Promise.all(FONT_FILES.map(([, , url]) => fetchBuffer(url)));
			const fonts = FONT_FILES.map(([family, weight], i) => ({ family, weight, base64: arrayBufferToBase64(buffers[i]) }));

			// The tile canvas needs the bold weights as web fonts of its own.
			await Promise.all(FONT_FILES.map(([family, weight], i) => {
				if (family !== 'Montserrat' || weight === '600') return null;
				const face = new FontFace(CANVAS_FAMILY, buffers[i].slice(0), { weight });
				document.fonts.add(face);
				return face.load();
			}));

			const logo = new Uint8Array(await fetchBuffer(logoUrl));
			return { fonts, logo };
		})().catch(err => {
			// A failed fetch must not stick: the next download tries again.
			assetsPromise = null;
			throw err;
		});
	}
	return assetsPromise;
}

function loadImage(src) {
	return new Promise((resolve, reject) => {
		const img = new window.Image();
		img.onload = () => resolve(img);
		img.onerror = () => reject(new Error('proof: could not load side image'));
		img.src = src;
	});
}

/* ---------- tiles (canvas) ---------- */

function roundedPath(ctx, x, y, w, h, r) {
	ctx.beginPath();
	ctx.moveTo(x + r, y);
	ctx.arcTo(x + w, y, x + w, y + h, r);
	ctx.arcTo(x + w, y + h, x, y + h, r);
	ctx.arcTo(x, y + h, x, y, r);
	ctx.arcTo(x, y, x + w, y, r);
	ctx.closePath();
}

// Letter by letter, so the spacing the template sets does not depend on
// ctx.letterSpacing, which older Safari does not have.
function spacedWidth(ctx, text, spacing) {
	let w = 0;
	for (const ch of text) w += ctx.measureText(ch).width + spacing;
	return w;
}

function fillSpaced(ctx, text, x, y, spacing) {
	for (const ch of text) {
		ctx.fillText(ch, x, y);
		x += ctx.measureText(ch).width + spacing;
	}
}

// The repeating "SHIRT-DISCOUNTER.NL / VOORBEELD" pattern: a 240 x 132 cell
// drawn at 42 x 23 mm, as in the template's background tile.
function drawWatermark(ctx, wPx, hPx) {
	const layer = document.createElement('canvas');
	layer.width = wPx;
	layer.height = hPx;
	const g = layer.getContext('2d');
	const cellW = 42 * PX;
	const cellH = 23 * PX;
	const u = Math.min(cellW / 240, cellH / 132);
	const ox = (cellW - 240 * u) / 2;
	const oy = (cellH - 132 * u) / 2;
	g.font = `700 ${11 * u}px "${CANVAS_FAMILY}"`;
	g.textBaseline = 'alphabetic';
	const lines = [
		['SHIRT-DISCOUNTER.NL', 6, 42, 'rgba(255,255,255,0.22)'],
		['SHIRT-DISCOUNTER.NL', 6, 42.8, 'rgba(16,24,32,0.09)'],
		['VOORBEELD', 70, 104, 'rgba(255,255,255,0.22)'],
		['VOORBEELD', 70, 104.8, 'rgba(16,24,32,0.09)']
	];
	for (let cy = 0; cy < hPx; cy += cellH) {
		for (let cx = 0; cx < wPx; cx += cellW) {
			g.save();
			g.beginPath();
			g.rect(cx, cy, cellW, cellH);
			g.clip();
			g.translate(cx + ox + 120 * u, cy + oy + 66 * u);
			g.rotate(-24 * Math.PI / 180);
			g.translate(-120 * u, -66 * u);
			for (const [text, x, y, color] of lines) {
				g.fillStyle = color;
				fillSpaced(g, text, x * u, y * u, 2.4 * u);
			}
			g.restore();
		}
	}
	// The template sets the whole pattern at 55% as one layer.
	ctx.save();
	ctx.globalAlpha = 0.55;
	ctx.drawImage(layer, 0, 0);
	ctx.restore();
}

// "VOORBEELD" across the middle of the tile.
function drawBand(ctx, wPx, hPx, sizePt) {
	const size = sizePt * PT * PX;
	const [asc, desc] = METRICS.Montserrat;
	ctx.save();
	ctx.font = `800 ${size}px "${CANVAS_FAMILY}"`;
	ctx.textBaseline = 'alphabetic';
	const spacing = 0.34 * size;
	const width = spacedWidth(ctx, 'VOORBEELD', spacing);
	ctx.translate(wPx / 2, hPx / 2);
	ctx.rotate(-24 * Math.PI / 180);
	ctx.fillStyle = 'rgba(255,255,255,0.32)';
	// A canvas shadow takes the fill's alpha along; doubled here so it comes
	// out at the template's 16%.
	ctx.shadowColor = 'rgba(16,24,32,0.5)';
	ctx.shadowBlur = 0.5 * PX;
	ctx.shadowOffsetY = 0.15 * PX;
	fillSpaced(ctx, 'VOORBEELD', -width / 2, (asc - desc) / 2 * size, spacing);
	ctx.restore();
}

// The part of the picture that is not white background, as a source rectangle.
// The product photos come with a wide white margin around the shirt, which in
// the large tile left the design small in a field of grey. A row or column
// counts only when enough of it is not white, so JPEG noise does not widen the
// box; a little of the margin is kept so the shirt does not touch the edges.
export function contentBounds(pixels, width, height, { threshold = 245, minShare = 0.01, margin = 0.04 } = {}) {
	const cols = new Uint32Array(width);
	const rows = new Uint32Array(height);
	for (let y = 0; y < height; y++) {
		for (let x = 0; x < width; x++) {
			const i = (y * width + x) * 4;
			if (pixels[i + 3] > 16 && Math.min(pixels[i], pixels[i + 1], pixels[i + 2]) < threshold) {
				cols[x]++;
				rows[y]++;
			}
		}
	}
	const first = (counts, min) => counts.findIndex(c => c > min);
	const last = (counts, min) => counts.length - 1 - counts.slice().reverse().findIndex(c => c > min);
	const x0 = first(cols, height * minShare);
	const y0 = first(rows, width * minShare);
	if (x0 === -1 || y0 === -1) return { x: 0, y: 0, w: width, h: height };
	const x1 = last(cols, height * minShare);
	const y1 = last(rows, width * minShare);
	const pad = Math.round(Math.max(x1 - x0, y1 - y0) * margin);
	const x = Math.max(0, x0 - pad);
	const y = Math.max(0, y0 - pad);
	return { x, y, w: Math.min(width, x1 + 1 + pad) - x, h: Math.min(height, y1 + 1 + pad) - y };
}

// Same, measured on a scaled-down copy for speed and scaled back up.
function trimmedSource(image) {
	const scale = Math.min(1, 300 / Math.max(image.width, image.height));
	const w = Math.max(1, Math.round(image.width * scale));
	const h = Math.max(1, Math.round(image.height * scale));
	const canvas = document.createElement('canvas');
	canvas.width = w;
	canvas.height = h;
	const ctx = canvas.getContext('2d');
	ctx.drawImage(image, 0, 0, w, h);
	const box = contentBounds(ctx.getImageData(0, 0, w, h).data, w, h);
	return { x: box.x / scale, y: box.y / scale, w: box.w / scale, h: box.h / scale };
}

// One tile as a JPEG: the grey field, the side's picture multiplied onto it,
// then watermark and band over the top. Corners outside the rounding are white,
// the colour of the page they sit on.
async function composeTile(image, tile, detail) {
	const wPx = Math.round(tile.w * PX);
	const hPx = Math.round(tile.h * PX);
	const canvas = document.createElement('canvas');
	canvas.width = wPx;
	canvas.height = hPx;
	const ctx = canvas.getContext('2d');
	ctx.fillStyle = COLOR.white;
	ctx.fillRect(0, 0, wPx, hPx);
	roundedPath(ctx, 0, 0, wPx, hPx, 3 * PX);
	ctx.clip();
	ctx.fillStyle = COLOR.field;
	ctx.fillRect(0, 0, wPx, hPx);

	// The large tile zooms in on the shirt; the small ones keep the whole
	// picture, so the sleeves stay cropped the way the template has them.
	const src = tile.large ? trimmedSource(image) : { x: 0, y: 0, w: image.width, h: image.height };
	const aspect = src.w / src.h;
	let bw, bh, bx, by;
	if (detail) {
		// Sleeves fill the tile and are cropped, top-aligned, as in the template.
		bw = Math.max(wPx, hPx * aspect);
		bh = bw / aspect;
		bx = (wPx - bw) / 2;
		by = 0;
	} else {
		const pad = tile.large ? [4, 4, 4, 4] : [8.4, 2.6, 2.6, 2.6];
		const cw = wPx - (pad[1] + pad[3]) * PX;
		const ch = hPx - (pad[0] + pad[2]) * PX;
		bw = Math.min(cw, ch * aspect);
		bh = bw / aspect;
		bx = pad[3] * PX + (cw - bw) / 2;
		by = pad[0] * PX + (ch - bh) / 2;
	}
	ctx.globalCompositeOperation = 'multiply';
	ctx.drawImage(image, src.x, src.y, src.w, src.h, bx, by, bw, bh);
	ctx.globalCompositeOperation = 'source-over';

	drawWatermark(ctx, wPx, hPx);
	drawBand(ctx, wPx, hPx, tile.large ? 26 : 8);
	return canvas.toDataURL('image/jpeg', 0.92);
}

/* ---------- page (pdf) ---------- */

function writer(pdf) {
	const use = st => {
		pdf.setFont(st.font, st.weight);
		pdf.setFontSize(st.size);
		pdf.setTextColor(st.color);
	};
	// Letter spacing in em, like the CSS; the spacing after the last letter
	// counts towards the width, as it does in the browser.
	const spacing = st => (st.ls || 0) * st.size * PT;
	const width = (text, st) => {
		use(st);
		return pdf.getTextWidth(text) + text.length * spacing(st);
	};
	const write = (text, x, baseline, st, align = 'left') => {
		const w = width(text, st);
		const left = align === 'right' ? x - w : x;
		pdf.text(text, left, baseline, { charSpace: spacing(st) });
		return left + w;
	};
	// Baseline for a single line centred on `center`, as in a flex pill.
	const centred = (center, st) => {
		const [asc, desc] = METRICS[st.font];
		return center + (asc - desc) / 2 * st.size * PT;
	};
	return { use, width, write, centred };
}

const STYLE = {
	kind: { font: 'Montserrat', weight: '800', size: 7, color: COLOR.ink, ls: 0.16 },
	dateLine: { font: 'NunitoSans', weight: '400', size: 7.2, color: COLOR.weak },
	eyebrow: { font: 'Montserrat', weight: '700', size: 7, color: COLOR.magenta, ls: 0.16 },
	title: { font: 'Montserrat', weight: '800', size: 21, color: COLOR.ink, ls: -0.025 },
	sub: { font: 'NunitoSans', weight: '400', size: 8, color: COLOR.weak },
	score: { font: 'Montserrat', weight: '800', size: 11, color: COLOR.ink, ls: -0.01 },
	scoreNote: { font: 'NunitoSans', weight: '400', size: 7, color: COLOR.weak },
	sideLarge: { font: 'Montserrat', weight: '700', size: 6.2, color: COLOR.white, ls: 0.06 },
	sideSmall: { font: 'Montserrat', weight: '700', size: 6.2, color: COLOR.text, ls: 0.06 },
	printed: { font: 'Montserrat', weight: '600', size: 6, color: COLOR.cyanDark },
	label: { font: 'Montserrat', weight: '700', size: 5.8, color: COLOR.weak, ls: 0.14 },
	labelContact: { font: 'Montserrat', weight: '700', size: 5.8, color: COLOR.cyanDark, ls: 0.14 },
	value: { font: 'Montserrat', weight: '800', size: 9.4, color: COLOR.ink, ls: -0.01 },
	mail: { font: 'Montserrat', weight: '700', size: 7.4, color: COLOR.cyanDark },
	why: { font: 'Montserrat', weight: '800', size: 9.4, color: COLOR.ink, ls: -0.01 },
	whyBrand: { font: 'Montserrat', weight: '800', size: 9.4, color: COLOR.cyan, ls: -0.01 },
	whyNote: { font: 'Montserrat', weight: '600', size: 6.6, color: COLOR.weak },
	uspTitle: { font: 'Montserrat', weight: '800', size: 7.4, color: COLOR.ink, ls: -0.01 },
	uspText: { font: 'NunitoSans', weight: '400', size: 6.3, color: COLOR.weak },
	footer: { font: 'NunitoSans', weight: '400', size: 6.4, color: COLOR.faint },
	footerName: { font: 'Montserrat', weight: '700', size: 6.6, color: COLOR.text },
	footerUrl: { font: 'Montserrat', weight: '700', size: 6.6, color: COLOR.ink }
};

// Icon outlines on the template's 24-unit grid.
const STAR = [[12, 2.6], [14.9, 8.6], [21.5, 9.5], [16.7, 14.1], [17.9, 20.6], [12, 17.5], [6.1, 20.6], [7.3, 14.1], [2.5, 9.5], [9.1, 8.6]];
const USPS = [
	{ title: 'Vanaf 1 stuk', lines: ['Eén shirt of vijfhonderd,', 'zelfde kwaliteit.'], x: 18.86,
		icon: [{ pts: [[9, 7], [13, 7], [13, 17]] }, { pts: [[9, 17], [17, 17]] }] },
	{ title: 'Gratis bestandscontrole', lines: ['Een specialist checkt je ontwerp', 'vóór druk.'], x: 57.47,
		icon: [{ circle: [11, 11, 7] }, { pts: [[20, 20], [16.4, 16.4]] }] },
	{ title: 'Snelste van het web', lines: ['Vóór 13:30 besteld, snel de', 'deur uit.'], x: 107.37,
		icon: [{ pts: [[13, 2], [4, 14], [10, 14], [9, 22], [18, 10], [12, 10], [13, 2]], closed: true }] },
	{ title: 'A-kwaliteit, B-prijs', lines: ['Al sinds 1998 textiel', 'bedrukken.'], x: 151.25,
		icon: [{ pts: [[12, 3], [14.7, 8.6], [20.8, 9.5], [16.4, 13.8], [17.4, 19.9], [12, 17], [6.6, 19.9], [7.6, 13.8], [3.2, 9.5], [9.3, 8.6]], closed: true }] }
];

function shape(pdf, pts, ox, oy, scale, style, closed) {
	const deltas = [];
	for (let i = 1; i < pts.length; i++) {
		deltas.push([(pts[i][0] - pts[i - 1][0]) * scale, (pts[i][1] - pts[i - 1][1]) * scale]);
	}
	pdf.lines(deltas, ox + pts[0][0] * scale, oy + pts[0][1] * scale, [1, 1], style, closed);
}

function drawPage(pdf, assets, data, tiles) {
	const t = writer(pdf);

	// Brand stripe.
	pdf.setFillColor(COLOR.cyan);
	pdf.rect(0, 0, 210 * 0.64, 3, 'F');
	pdf.setFillColor(COLOR.magenta);
	pdf.rect(210 * 0.64, 0, 210 * 0.36, 3, 'F');

	// Header: logo left, kind of document and date right, rule under it.
	const logoH = 50 * 106 / 700;
	pdf.addImage(assets.logo, 'PNG', 14, 12.84 + (7.6 - logoH) / 2, 50, logoH);
	const kindLeft = 196 - t.width('ONTWERPVOORBEELD', STYLE.kind);
	t.write('ONTWERPVOORBEELD', kindLeft, t.centred(14.335, STYLE.kind), STYLE.kind);
	pdf.setFillColor(COLOR.magenta);
	pdf.circle(kindLeft - 1.6 - 0.9, 14.335, 0.9, 'F');
	t.write(`Gemaakt op ${dutchDate(data.date)}`, 196, 21.26, STYLE.dateLine, 'right');
	pdf.setFillColor(COLOR.border);
	pdf.rect(14, 28.26, 182, 0.3, 'F');

	// Title and the review score.
	t.write('ZELF ONTWORPEN', 14, 39.79, STYLE.eyebrow);
	t.write('Jouw ontwerp', 14, 50.19, STYLE.title);
	t.write('Gemaakt in de ontwerptool van Shirt-Discounter.nl', 14, 56.22, STYLE.sub);
	pdf.setFillColor(COLOR.star);
	for (let i = 0; i < 5; i++) shape(pdf, STAR, 172.82 + i * 4.795, 47.31, 4 / 24, 'F', true);
	const note = '/ 10 · 11.340 reviews';
	const scoreLeft = 196 - t.width(note, STYLE.scoreNote) - 0.6 - t.width('9,2', STYLE.score);
	t.write(note, t.write('9,2', scoreLeft, 56.08, STYLE.score) + 0.6, 56.08, STYLE.scoreNote);

	// Tiles: pictures were composed beforehand, the labels go on top here.
	tiles.forEach(tile => {
		pdf.addImage(tile.picture, 'JPEG', tile.x, tile.y, tile.w, tile.h);
		const nameStyle = tile.large ? STYLE.sideLarge : STYLE.sideSmall;
		const name = sideName(tile.side).toUpperCase();
		const pillY = tile.y + 2.4;
		const pillW = 2.2 + t.width(name, nameStyle) + 2.2;
		pdf.setFillColor(tile.large ? COLOR.ink : COLOR.white);
		pdf.roundedRect(tile.x + 2.4, pillY, pillW, 5, 2.5, 2.5, 'F');
		t.write(name, tile.x + 2.4 + 2.2, t.centred(pillY + 2.5, nameStyle), nameStyle);

		if (!tile.printed) return;
		const right = tile.x + tile.w - 2.4;
		if (tile.large) {
			const w = 2.2 + 2.4 + 1.2 + t.width('Bedrukt', STYLE.printed) + 2.2;
			pdf.setFillColor(COLOR.white);
			pdf.roundedRect(right - w, pillY, w, 5, 2.5, 2.5, 'F');
			printedMark(pdf, right - w + 2.2, pillY + 1.3);
			t.write('Bedrukt', right - w + 2.2 + 2.4 + 1.2, t.centred(pillY + 2.5, STYLE.printed), STYLE.printed);
		} else {
			pdf.setFillColor(COLOR.white);
			pdf.circle(right - 2.5, pillY + 2.5, 2.5, 'F');
			printedMark(pdf, right - 2.5 - 1.2, pillY + 1.3);
		}
	});

	// Cards under the pictures.
	data.cards.forEach(card => {
		pdf.setFillColor(card.contact ? COLOR.cyanLight : COLOR.field);
		pdf.roundedRect(card.x, card.y, card.w, card.h, 3, 3, 'F');
		t.write(card.label, card.x + 4, card.y + 4.98, card.contact ? STYLE.labelContact : STYLE.label);
		card.lines.forEach((line, i) => t.write(line, card.x + 4, card.y + 10.41 + i * 3.98, STYLE.value));
		if (card.contact) t.write('info@shirt-discounter.nl', card.x + 4, card.y + 14.8, STYLE.mail);
	});

	// Why Shirt-Discounter.nl.
	const box = { x: 14, y: 251.34, w: 182, h: 26.89 };
	pdf.saveGraphicsState();
	pdf.roundedRect(box.x + 0.35, box.y + 0.35, box.w - 0.7, box.h - 0.7, 2.65, 2.65, null);
	pdf.clip();
	pdf.discardPath();
	pdf.setFillColor(COLOR.cyan);
	pdf.rect(box.x, box.y, 1.55, box.h * 0.64, 'F');
	pdf.setFillColor(COLOR.magenta);
	pdf.rect(box.x, box.y + box.h * 0.64, 1.55, box.h * 0.36, 'F');
	pdf.restoreGraphicsState();
	pdf.setDrawColor(COLOR.border);
	pdf.setLineWidth(0.35);
	pdf.roundedRect(box.x + 0.175, box.y + 0.175, box.w - 0.35, box.h - 0.35, 2.825, 2.825, 'S');

	t.write('Shirt-Discounter.nl', t.write('Waarom ', 18.86, 258.65, STYLE.why), 258.65, STYLE.whyBrand);
	t.write('Al ruim 25 jaar textiel bedrukken', 191.14, 258.65, STYLE.whyNote, 'right');

	const iconScale = 4 / 24;
	USPS.forEach(usp => {
		pdf.setFillColor(COLOR.cyan);
		pdf.circle(usp.x + 3.7, 262.72 + 3.7, 3.7, 'F');
		const ox = usp.x + 1.7;
		const oy = 262.72 + 1.7;
		pdf.setDrawColor(COLOR.white);
		pdf.setLineWidth(2.2 * iconScale);
		pdf.setLineCap('round');
		pdf.setLineJoin('round');
		usp.icon.forEach(part => {
			if (part.circle) {
				const [cx, cy, r] = part.circle;
				pdf.circle(ox + cx * iconScale, oy + cy * iconScale, r * iconScale, 'S');
			} else {
				shape(pdf, part.pts, ox, oy, iconScale, 'S', !!part.closed);
			}
		});
		t.write(usp.title, usp.x + 9.79, 265.5, STYLE.uspTitle);
		usp.lines.forEach((line, i) => t.write(line, usp.x + 9.79, 269.26 + i * 3.11, STYLE.uspText));
	});
	pdf.setLineCap('butt');
	pdf.setLineJoin('miter');

	// Footer.
	t.write('Ontwerpvoorbeeld, geen offerte of factuur. Kleuren op scherm en in druk kunnen licht afwijken.', 14, 284.61, STYLE.footer);
	t.write(' · Cavalier 10, 3897 AA Zeewolde', t.write('Shirt-Discounter.nl', 14, 287.99, STYLE.footerName), 287.99, STYLE.footer);
	t.write('www.shirt-discounter.nl', 196, 287.99, STYLE.footerUrl, 'right');
}

// The small "printed" square: cyan outline on a light cyan fill.
function printedMark(pdf, x, y) {
	pdf.setFillColor('#E6F5FC');
	pdf.setDrawColor(COLOR.cyan);
	pdf.setLineWidth(0.3);
	pdf.roundedRect(x + 0.15, y + 0.15, 2.1, 2.1, 0.35, 0.35, 'FD');
}

// The cards under the pictures, bottom-aligned on the box below them. A value
// too long for its card wraps, and the cards grow - and the pictures shrink -
// to make room, as the template's flex layout does.
function layoutCards(pdf, details) {
	const t = writer(pdf);
	const fields = [['ARTIKEL', details.sku], ['KLEUR', details.color]];
	if (details.printMethod) fields.push(['BEDRUKKING', details.printMethod]);
	const w = (182 - 52 - fields.length * 4) / fields.length;
	t.use(STYLE.value);
	const cards = fields.map(([label, value], i) => ({
		label, x: 14 + i * (w + 4), w,
		lines: pdf.splitTextToSize(String(value || '–'), w - 8)
	}));
	cards.push({ label: 'VRAGEN?', x: 144, w: 52, lines: ['036 – 2000 204'], contact: true });
	const contentH = Math.max(...cards.map(c => c.contact ? 8.31 : c.lines.length * 3.98));
	const h = 3.4 + 2.05 + 1.8 + contentH + 3.4;
	const y = 251.34 - 5 - h;
	cards.forEach(c => { c.y = y; c.h = h; });
	return { cards, top: y };
}

function registerFonts(pdf, fonts) {
	fonts.forEach(({ family, weight, base64 }) => {
		const file = `${family}-${weight}.ttf`;
		pdf.addFileToVFS(file, base64);
		pdf.addFont(file, family, weight);
	});
}

/**
 * Builds the proof and returns the jsPDF document.
 *
 * sides: [{ side: 'front' | 'back' | 'left' | 'right', image: dataURL, printed: bool }]
 * details: { sku, color, printMethod } - printMethod may be left out; the card
 * for it is then left off rather than shown empty.
 */
export async function buildProof(sides, details, date = new Date()) {
	const assets = await loadAssets();
	const pdf = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4', compress: true });
	registerFonts(pdf, assets.fonts);
	pdf.setProperties({ title: `Ontwerpvoorbeeld ${details.sku || ''}`.trim(), author: 'Shirt-Discounter.nl', creator: 'Shirt-Discounter.nl ontwerptool' });

	const { cards, top } = layoutCards(pdf, details);
	const ordered = orderSides(sides);
	const rects = tileLayout(ordered.length, 63.52, top - 4);
	const tiles = await Promise.all(ordered.map(async (s, i) => {
		const rect = rects[i];
		const image = await loadImage(s.image);
		const detail = !rect.large && (s.side === 'left' || s.side === 'right');
		return { ...rect, side: s.side, printed: s.printed, picture: await composeTile(image, rect, detail) };
	}));

	drawPage(pdf, assets, { date, cards }, tiles);
	return pdf;
}
